import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import jsQR from 'jsqr';
import { AUTH_URL } from '../utils/authApi';
import { lastUsernameQuery, getDeviceToken } from '../utils/auth';
import { cameraFoutmelding } from '../utils/cameraFout';
import { qrStatus, serverFout } from '../utils/qrStatus';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8009';

// Convert base64url to ArrayBuffer
function base64urlToBuffer(base64url) {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const rawData = atob(base64);
  return Uint8Array.from(rawData, c => c.charCodeAt(0)).buffer;
}

// Convert ArrayBuffer to base64url
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (const byte of bytes) {
    str += String.fromCharCode(byte);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Extract QR code from URL like https://havuncore.havun.nl/qr/qr_<code>
 */
function extractQrCode(value) {
  try {
    const url = new URL(value);
    // Laravel codes look like qr_<alphanumeric> (QrAuthService).
    const match = url.pathname.match(/^\/qr\/(qr_[A-Za-z0-9]+)$/);
    return match ? match[1] : null;
  } catch {
    // Not a URL — try raw code
    if (/^qr_[A-Za-z0-9]+$/.test(value)) return value;
    return null;
  }
}

export function QrScanner({ token: _token, onClose, onSetupBiometric }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const [status, setStatus] = useState('checking'); // checking, starting, scanning, scanned, approving, approved, no_biometric, error
  const [error, setError] = useState('');
  // Alleen geschreven: de scan levert device-info die (nog) nergens getoond wordt.
  const [, setDeviceInfo] = useState(null);
  const [streamReady, setStreamReady] = useState(false);

  useEffect(() => {
    checkBiometricAndStart();

    return () => stopCamera();
    // Bewust alleen bij mount: dit start de camera. Opnieuw draaien zou de stream
    // herstarten terwijl de gebruiker aan het scannen is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkBiometricAndStart = async () => {
    try {
      const response = await axios.get(`${AUTH_URL}/api/auth/webauthn/available${lastUsernameQuery()}`);
      if (!response.data.available) {
        setStatus('no_biometric');
        return;
      }
    } catch {
      // Can't check, try anyway
    }
    startCamera();
  };

  const startCamera = async () => {
    // The <video> element only exists from 'starting' onwards. Asking for the
    // camera while still on 'checking' left videoRef empty, so the stream was
    // opened -- camera light on -- but never attached and the screen sat on
    // "Controleren..." forever. Render the element first, attach in the effect
    // below once React has handed us the ref.
    setStatus('starting');
    // A retry ("Opnieuw scannen") must flip streamReady false -> true again, or the effect that
    // attaches the stream to <video> never re-runs: camera on, nothing scanned (25-10-2026).
    setStreamReady(false);

    try {
      // Simpele constraints zoals VPDUpdate — geen width/height (kan camera blokkeren)
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      setStreamReady(true);
    } catch (err) {
      setError(cameraFoutmelding(err));
      setStatus('error');
    }
  };

  useEffect(() => {
    if (!streamReady || !videoRef.current || !streamRef.current) return;

    let cancelled = false;
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play()
      .then(() => {
        if (cancelled) return;
        setStatus('scanning');
        startScanning();
      })
      .catch(() => {
        if (cancelled) return;
        setError('Camerabeeld kon niet starten.');
        setStatus('error');
      });

    return () => { cancelled = true; };
    // Hangt bewust alleen aan `streamReady`: `startScanning` erbij zou elke render een
    // nieuwe scanlus starten op dezelfde stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamReady]);

  const stopCamera = () => {
    if (scanIntervalRef.current?.stop) scanIntervalRef.current.stop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setStreamReady(false);
  };

  const startScanning = () => {
    // jsQR via canvas + requestAnimationFrame (same pattern as VPDUpdate)
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    let scanning = true;

    const scanFrame = () => {
      if (!scanning || !videoRef.current || videoRef.current.readyState < 2) {
        if (scanning) requestAnimationFrame(scanFrame);
        return;
      }

      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = jsQR(imageData.data, imageData.width, imageData.height);

      if (result && result.data) {
        scanning = false;
        const code = extractQrCode(result.data);
        if (code) {
          handleScanned(code);
        } else {
          scanning = true;
          requestAnimationFrame(scanFrame);
        }
      } else {
        requestAnimationFrame(scanFrame);
      }
    };

    requestAnimationFrame(scanFrame);

    // Store stop function in ref for cleanup
    scanIntervalRef.current = { stop: () => { scanning = false; } };
  };

  const handleScanned = async (code) => {
    setStatus('scanned');
    stopCamera();

    try {
      const { status, deviceInfo } = await qrStatus(code);

      if (status !== 'pending') {
        setError(status === 'expired' ? 'QR code verlopen — vernieuw hem op de pc' : 'QR code ongeldig of verlopen');
        setStatus('error');
        return;
      }

      setDeviceInfo(deviceInfo);
      // Auto-approve with biometric
      await handleApprove(code);
    } catch (err) {
      setError(serverFout(err, 'QR code ongeldig of verlopen'));
      setStatus('error');
    }
  };

  const handleApprove = async (code) => {
    setStatus('approving');

    try {
      // Get WebAuthn options
      const optionsResponse = await axios.get(`${AUTH_URL}/api/auth/webauthn/login-options${lastUsernameQuery()}`);
      const options = optionsResponse.data;

      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        timeout: options.timeout,
        rpId: options.rpId,
        userVerification: options.userVerification,
      };

      if (options.allowCredentials) {
        publicKeyOptions.allowCredentials = options.allowCredentials.map(c => ({
          id: base64urlToBuffer(c.id),
          type: c.type,
          transports: c.transports,
        }));
      }

      // Trigger biometric
      const credential = await navigator.credentials.get({ publicKey: publicKeyOptions });

      // Approve
      await axios.post(`${API_URL}/api/auth/qr/${code}/approve`, {
        credential: {
          id: credential.id,
          rawId: bufferToBase64url(credential.rawId),
          type: credential.type,
          response: {
            authenticatorData: bufferToBase64url(credential.response.authenticatorData),
            clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
            signature: bufferToBase64url(credential.response.signature),
          },
        },
      }, { headers: { Authorization: `Bearer ${getDeviceToken()}` } });

      setStatus('approved');
      setTimeout(onClose, 2000);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Biometrische verificatie geannuleerd');
      } else {
        setError(serverFout(err, 'Goedkeuring mislukt'));
      }
      setStatus('error');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: '#000', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 20px', background: 'rgba(0,0,0,0.8)',
      }}>
        <h2 style={{ color: 'white', fontSize: '18px', fontWeight: '600', margin: 0 }}>
          QR Scanner
        </h2>
        <button
          onClick={() => { stopCamera(); onClose(); }}
          style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
            color: 'white', width: '36px', height: '36px', fontSize: '20px', cursor: 'pointer',
          }}
        >×</button>
      </div>

      {/* Camera / Status */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {status === 'checking' && (
          <div style={{ textAlign: 'center', color: 'white' }}>
            <div className="animate-spin" style={{
              width: '48px', height: '48px', border: '4px solid rgba(255,255,255,0.3)',
              borderTopColor: 'white', borderRadius: '50%', margin: '0 auto 16px',
            }} />
            <p style={{ fontSize: '16px' }}>Controleren...</p>
          </div>
        )}

        {status === 'no_biometric' && (
          <div style={{ textAlign: 'center', color: 'white', padding: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔐</div>
            <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Biometrie nog niet ingesteld
            </p>
            <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '24px' }}>
              Stel eerst Face ID of vingerafdruk in om de PC via QR in te loggen.
            </p>
            <button
              onClick={() => { onClose(); onSetupBiometric?.(); }}
              style={{
                background: '#3b82f6', border: 'none', borderRadius: '10px',
                color: 'white', padding: '14px 28px', fontSize: '16px', cursor: 'pointer',
                fontWeight: '600',
              }}
            >Biometrie instellen</button>
          </div>
        )}

        {(status === 'starting' || status === 'scanning') && (
          <>
            <video
              ref={videoRef}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              autoPlay
              playsInline
            />
            {/* Scan overlay */}
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{
                width: '250px', height: '250px',
                border: '3px solid rgba(255,255,255,0.8)',
                borderRadius: '20px',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)',
              }} />
            </div>
            <p style={{
              position: 'absolute', bottom: '80px', left: 0, right: 0,
              textAlign: 'center', color: 'white', fontSize: '16px',
            }}>
              Richt op de QR code op het PC scherm
            </p>
          </>
        )}

        {status === 'scanned' && (
          <div style={{ textAlign: 'center', color: 'white' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <p style={{ fontSize: '18px' }}>QR gescand!</p>
          </div>
        )}

        {status === 'approving' && (
          <div style={{ textAlign: 'center', color: 'white' }}>
            <div className="animate-spin" style={{
              width: '48px', height: '48px', border: '4px solid rgba(255,255,255,0.3)',
              borderTopColor: 'white', borderRadius: '50%', margin: '0 auto 16px',
            }} />
            <p style={{ fontSize: '18px' }}>Biometrie verwerken...</p>
          </div>
        )}

        {status === 'approved' && (
          <div style={{ textAlign: 'center', color: 'white' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
            <p style={{ fontSize: '20px', fontWeight: '600' }}>Desktop is ingelogd!</p>
          </div>
        )}

        {status === 'error' && (
          <div style={{ textAlign: 'center', color: 'white', padding: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
            <p style={{ fontSize: '16px', color: '#f87171', marginBottom: '20px' }}>{error}</p>
            <button
              onClick={() => { setError(''); startCamera(); }}
              style={{
                background: '#3b82f6', border: 'none', borderRadius: '10px',
                color: 'white', padding: '12px 28px', fontSize: '16px', cursor: 'pointer',
              }}
            >Opnieuw scannen</button>
          </div>
        )}
      </div>
    </div>
  );
}
